import { i18n } from 'i18next';
import React from 'react';
import { TransProps as BaseTransProps, Trans } from 'react-i18next';
import { DurationProps } from './formatters/relativeTimeFormatters';
import { TFunction } from './types/TFunction';

/**
 * Dynamic values used in translation strings must be of a restricted number of
 * types.
 */
export type DynamicValue = string | number | Date | string[] | number[] | Date[] | DurationProps;

/**
 * The `TransWrapper` will allow arbitrary properties whose values are
 * dynamically inserted into the translation. This prevents the need for the use
 * of the `values` property to pass in dynamic values in the translated string.
 */
export type DynamicValues = Record<string, DynamicValue>;

type AllowedTransProps<K extends keyof BaseTransProps<string>> = Pick<BaseTransProps<string>, K>;

interface DisallowedTransProps {
  /**
   * Do not use `values`. Instead, provide the key/value pairs as props to
   * `Trans` directly.
   *
   * @example
   * Old: <Trans values={{ foo: 'bar' }} />
   * New: <Trans foo="bar" />
   */
  values?: never;
  /**
   * Do not use `t`. Instead, import "Trans" from a localization.ts
   * file, and it will have t built-in because of how our custom TransWrapper works.
   */
  t?: never;
}

/**
 * Generates prop types for a `Trans` wrapper component
 */
export type TransWrapperPropsFactory<
  AllowedBasePropNames extends Exclude<keyof BaseTransProps<string>, keyof DisallowedTransProps>,
> =
  | AllowedTransProps<AllowedBasePropNames>
  | (DynamicValues &
      // Re-declaring the allowed props here prevents TypeScript from mixing the allowed prop types with `DynamicValue`.
      AllowedTransProps<AllowedBasePropNames> &
      DisallowedTransProps);

/**
 * The `TransWrapper` can forward some allowed properties, as well as accept
 * arbitrary values to use when filling in dynamic values.
 */
export type TransWrapperProps = TransWrapperPropsFactory<
  'children' | 'count' | 'components' | 'i18nKey'
>;

/**
 * The signature of the `TransWrapper` component, which is both the functional
 * component and a `displayName` to make diagnostics easier.
 */
export type TransWrapperType = ((props: TransWrapperProps) => JSX.Element) & {
  displayName: string;
};

export const generateTrans = (i18nInstance: i18n, useTranslation: () => { t: TFunction }) => {
  const TransWrapper: TransWrapperType = ({
    children,
    components,
    count,
    i18nKey,
    ...nonCountValues
  }) => {
    const { t } = useTranslation();

    /*
     * Assemble props in a way where we avoid passing any props undefined.
     * Otherwise get errors with values=undefined where it unexpectedly fires the formatters.
     */
    const props = { i18n: i18nInstance, t } as BaseTransProps<string>;
    if (count !== undefined) props.count = count;
    if (i18nKey) props.i18nKey = i18nKey;
    if (components) props.components = components;
    const hasValues = Object.keys(nonCountValues).length;
    /*
     * The `Trans` component immediately re-spreads the values internally, so
     * any form of memoization here would be immediately defeated. If that
     * implementation changes, we can insert a hash-based memoization strategy
     * here. For now, though, it would be unnecessary overhead.
     */
    if (count !== undefined && hasValues) {
      props.values = { ...nonCountValues, count };
    } else if (hasValues) {
      props.values = nonCountValues;
    }

    return <Trans {...props}>{children}</Trans>;
  };
  TransWrapper.displayName = 'TransWrapper';

  return TransWrapper;
};
